import { z } from 'zod';
/**
 * Contracts for the generic master-data import surface.
 *
 * Subjects are the first consumer (AIMSIS-19378); grade levels and classrooms
 * reuse the same flow. The field list is entity-specific, so `field` in a
 * column mapping is a plain string — the API validates it against the
 * entity's spec before it reaches a row.
 *
 * Flow: download template → upload → `PreviewMasterImportRequest` → review
 * rows + fix mapping → `ConfirmMasterImportRequest`. The preview is persisted
 * server-side and the confirm carries its digest, so an import can only write
 * the rows the reviewer actually saw.
 */
/** A reviewer's explicit column → field assignment for one column. */
export const MasterImportColumnMapping = z.object({
    /** 0-based worksheet column index the assignment applies to. */
    column: z.number().int().nonnegative().max(63),
    /** A spec field key, or a sentinel: `unmapped` clears auto-detection for the
     * column, `ignore` drops it from the import entirely. */
    field: z.string().min(1).max(64),
});
export const PreviewMasterImportRequest = z.object({
    fileName: z.string().trim().min(1).max(255),
    /** Base64 of the XLS/XLSX/CSV payload. Sized well above the parser's own
     * byte cap so the parser — not the schema — owns the size message. */
    workbookBase64: z.string().min(1).max(8_000_000),
    /** The reviewer's explicit column assignments; untouched columns stay
     * absent so the parser keeps its own alias detection for them. */
    fieldMapping: z.array(MasterImportColumnMapping).max(64).optional(),
    /** 1-based sheet row the reviewer picked as the header, sent after a
     * preview reported `headerRowDetected: false` or offered
     * `headerCandidates`. Column mapping alone cannot repair a wrong header —
     * the header decides which cells are read as fields at all. */
    headerRow: z.number().int().positive().max(10_000).optional(),
}).strict();
export const ConfirmMasterImportRequest = z.object({
    digest: z.string().regex(/^[a-f0-9]{64}$/),
    /** Finalization stays blocked while any row is invalid. The reviewer clears
     * the block either by fixing the workbook and previewing again, or by
     * explicitly skipping the remaining rows and importing only the valid
     * ones. */
    skipInvalidRows: z.boolean().default(false),
}).strict();
export const MasterImportIdParams = z.object({
    importId: z.string().uuid(),
});
//# sourceMappingURL=master-data-import.js.map